/**
 * IndexedDB 数据库管理
 * 墨 · 阅读 - 本地存储方案
 */

const DB_NAME = 'InkReaderDB';
const DB_VERSION = 5;

const STORES = {
  BOOKS: 'books',
  THOUGHTS: 'thoughts',
  HIGHLIGHTS: 'highlights',
  ESSAYS: 'essays',
  READING_PROGRESS: 'readingProgress',
  BOOKMARKS: 'bookmarks',
  MUSIC: 'music',
  PODCASTS: 'podcasts'
};

let db = null;

/**
 * 初始化数据库
 */
export async function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      const error = request.error;
      if (error?.name === 'VersionError') {
        const retryRequest = indexedDB.open(DB_NAME);
        retryRequest.onerror = () => reject(retryRequest.error);
        retryRequest.onsuccess = () => {
          db = retryRequest.result;
          resolve(db);
        };
        return;
      }
      reject(error);
    };
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // 书籍存储
      if (!database.objectStoreNames.contains(STORES.BOOKS)) {
        const booksStore = database.createObjectStore(STORES.BOOKS, { keyPath: 'id' });
        booksStore.createIndex('title', 'title', { unique: false });
        booksStore.createIndex('author', 'author', { unique: false });
        booksStore.createIndex('addedAt', 'addedAt', { unique: false });
        booksStore.createIndex('lastReadAt', 'lastReadAt', { unique: false });
      }

      // 思索存储
      if (!database.objectStoreNames.contains(STORES.THOUGHTS)) {
        const thoughtsStore = database.createObjectStore(STORES.THOUGHTS, { keyPath: 'id' });
        thoughtsStore.createIndex('bookId', 'bookId', { unique: false });
        thoughtsStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // 涂鸦存储
      if (!database.objectStoreNames.contains(STORES.HIGHLIGHTS)) {
        const highlightsStore = database.createObjectStore(STORES.HIGHLIGHTS, { keyPath: 'id' });
        highlightsStore.createIndex('bookId', 'bookId', { unique: false });
        highlightsStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // 随笔存储
      if (!database.objectStoreNames.contains(STORES.ESSAYS)) {
        const essaysStore = database.createObjectStore(STORES.ESSAYS, { keyPath: 'id' });
        essaysStore.createIndex('bookId', 'bookId', { unique: false });
        essaysStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // 阅读进度存储
      if (!database.objectStoreNames.contains(STORES.READING_PROGRESS)) {
        database.createObjectStore(STORES.READING_PROGRESS, { keyPath: 'bookId' });
      }

      // 书签存储
      if (!database.objectStoreNames.contains(STORES.BOOKMARKS)) {
        const bookmarksStore = database.createObjectStore(STORES.BOOKMARKS, { keyPath: 'id' });
        bookmarksStore.createIndex('bookId', 'bookId', { unique: false });
        bookmarksStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // 音乐存储
      if (!database.objectStoreNames.contains(STORES.MUSIC)) {
        const musicStore = database.createObjectStore(STORES.MUSIC, { keyPath: 'id' });
        musicStore.createIndex('title', 'title', { unique: false });
        musicStore.createIndex('artist', 'artist', { unique: false });
        musicStore.createIndex('addedAt', 'addedAt', { unique: false });
        musicStore.createIndex('favorite', 'favorite', { unique: false });
      }

      if (!database.objectStoreNames.contains(STORES.PODCASTS)) {
        const podcastsStore = database.createObjectStore(STORES.PODCASTS, { keyPath: 'id' });
        podcastsStore.createIndex('feedUrl', 'feedUrl', { unique: true });
        podcastsStore.createIndex('title', 'title', { unique: false });
        podcastsStore.createIndex('addedAt', 'addedAt', { unique: false });
      }
    };
  });
}

/**
 * 获取数据库实例
 */
function getDB() {
  if (!db) {
    throw new Error('Database not initialized');
  }
  return db;
}

/**
 * 生成唯一ID
 */
export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function isValidIDBKey(key) {
  if (key === null || key === undefined) return false;
  if (typeof key === 'string') return key.trim().length > 0;
  if (typeof key === 'number') return Number.isFinite(key);
  if (key instanceof Date) return !Number.isNaN(key.getTime());
  if (Array.isArray(key)) return key.every(isValidIDBKey);
  return false;
}

function assertValidIDBKey(key, label) {
  if (!isValidIDBKey(key)) {
    throw new TypeError(`${label} is not a valid IndexedDB key`);
  }
}

// ========================================
// 书籍操作
// ========================================

export async function addBook(book) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.BOOKS], 'readwrite');
    const store = transaction.objectStore(STORES.BOOKS);

    const bookData = {
      id: generateId(),
      title: book.title || '未知书名',
      author: book.author || '未知作者',
      cover: book.cover || null,
      fileData: book.fileData,
      fileType: book.fileType,
      fileSize: book.fileSize,
      addedAt: new Date().toISOString(),
      plannedAt: null,
      lastReadAt: null,
      progress: 0
    };

    const request = store.add(bookData);
    request.onsuccess = () => resolve(bookData);
    request.onerror = () => reject(request.error);
  });
}

export async function getBook(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    try {
      assertValidIDBKey(id, 'Book id');
      const transaction = database.transaction([STORES.BOOKS], 'readonly');
      const store = transaction.objectStore(STORES.BOOKS);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
}

export async function getAllBooks() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.BOOKS], 'readonly');
    const store = transaction.objectStore(STORES.BOOKS);
    const request = store.getAll();
    request.onsuccess = () => {
      const books = request.result.sort((a, b) =>
        new Date(b.addedAt) - new Date(a.addedAt)
      );
      resolve(books);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function updateBook(idOrBook, updates) {
  const database = getDB();
  return new Promise(async (resolve, reject) => {
    try {
      const id = (idOrBook && typeof idOrBook === 'object') ? idOrBook.id : idOrBook;
      const resolvedUpdates = updates === undefined && idOrBook && typeof idOrBook === 'object'
        ? idOrBook
        : updates;

      assertValidIDBKey(id, 'Book id');

      const book = await getBook(id);
      if (!book) {
        reject(new Error('Book not found'));
        return;
      }

      const transaction = database.transaction([STORES.BOOKS], 'readwrite');
      const store = transaction.objectStore(STORES.BOOKS);
      const updatedBook = resolvedUpdates ? { ...book, ...resolvedUpdates, id } : book;
      const request = store.put(updatedBook);
      request.onsuccess = () => resolve(updatedBook);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
}

export async function deleteBook(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    try {
      assertValidIDBKey(id, 'Book id');
      const transaction = database.transaction([STORES.BOOKS], 'readwrite');
      const store = transaction.objectStore(STORES.BOOKS);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
}

export async function updateBooksOrder(orderedIds) {
  const database = getDB();
  return new Promise(async (resolve, reject) => {
    try {
      const transaction = database.transaction([STORES.BOOKS], 'readwrite');
      const store = transaction.objectStore(STORES.BOOKS);

      for (let i = 0; i < orderedIds.length; i++) {
        assertValidIDBKey(orderedIds[i], 'Book id');
        const getRequest = store.get(orderedIds[i]);
        await new Promise((res, rej) => {
          getRequest.onsuccess = () => {
            const book = getRequest.result;
            if (book) {
              book.sortOrder = i;
              store.put(book);
            }
            res();
          };
          getRequest.onerror = () => rej(getRequest.error);
        });
      }

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    } catch (error) {
      reject(error);
    }
  });
}

// ========================================
// 思索操作
// ========================================

export async function addThought(thought) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THOUGHTS], 'readwrite');
    const store = transaction.objectStore(STORES.THOUGHTS);

    const thoughtData = {
      id: generateId(),
      bookId: thought.bookId || null,
      bookTitle: thought.bookTitle || null,
      content: thought.content,
      tags: thought.tags || [],
      images: thought.images || [],
      audios: thought.audios || [],
      videos: thought.videos || [],
      references: thought.references || [],
      location: thought.location || null,
      pinned: thought.pinned || false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const request = store.add(thoughtData);
    request.onsuccess = () => resolve(thoughtData);
    request.onerror = () => reject(request.error);
  });
}

export async function getThought(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THOUGHTS], 'readonly');
    const store = transaction.objectStore(STORES.THOUGHTS);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function updateThought(id, updates) {
  const database = getDB();
  return new Promise(async (resolve, reject) => {
    const thought = await getThought(id);
    if (!thought) {
      reject(new Error('Thought not found'));
      return;
    }

    const transaction = database.transaction([STORES.THOUGHTS], 'readwrite');
    const store = transaction.objectStore(STORES.THOUGHTS);
    const updatedThought = {
      ...thought,
      ...updates,
      updatedAt: new Date().toISOString()
    };
    const request = store.put(updatedThought);
    request.onsuccess = () => resolve(updatedThought);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllThoughts() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THOUGHTS], 'readonly');
    const store = transaction.objectStore(STORES.THOUGHTS);
    const request = store.getAll();
    request.onsuccess = () => {
      const thoughts = request.result.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      resolve(thoughts);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function getThoughtsByBook(bookId) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THOUGHTS], 'readonly');
    const store = transaction.objectStore(STORES.THOUGHTS);
    const index = store.index('bookId');
    const request = index.getAll(bookId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteThought(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THOUGHTS], 'readwrite');
    const store = transaction.objectStore(STORES.THOUGHTS);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// 涂鸦操作
// ========================================

export async function addHighlight(highlight) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.HIGHLIGHTS], 'readwrite');
    const store = transaction.objectStore(STORES.HIGHLIGHTS);

    const highlightData = {
      id: generateId(),
      bookId: highlight.bookId,
      bookTitle: highlight.bookTitle,
      text: highlight.text,
      note: highlight.note || '',
      location: highlight.location || null,
      color: highlight.color || '#ffffff',
      style: highlight.style || null,
      createdAt: new Date().toISOString()
    };

    const request = store.add(highlightData);
    request.onsuccess = () => resolve(highlightData);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllHighlights() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.HIGHLIGHTS], 'readonly');
    const store = transaction.objectStore(STORES.HIGHLIGHTS);
    const request = store.getAll();
    request.onsuccess = () => {
      const highlights = request.result.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      resolve(highlights);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function getHighlightsByBook(bookId) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.HIGHLIGHTS], 'readonly');
    const store = transaction.objectStore(STORES.HIGHLIGHTS);
    const index = store.index('bookId');
    const request = index.getAll(bookId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function updateHighlight(id, updates) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.HIGHLIGHTS], 'readonly');
    const store = transaction.objectStore(STORES.HIGHLIGHTS);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const highlight = getRequest.result;
      if (!highlight) {
        reject(new Error('Highlight not found'));
        return;
      }

      const writeTransaction = database.transaction([STORES.HIGHLIGHTS], 'readwrite');
      const writeStore = writeTransaction.objectStore(STORES.HIGHLIGHTS);
      const updatedHighlight = { ...highlight, ...updates };
      const putRequest = writeStore.put(updatedHighlight);
      putRequest.onsuccess = () => resolve(updatedHighlight);
      putRequest.onerror = () => reject(putRequest.error);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

export async function deleteHighlight(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.HIGHLIGHTS], 'readwrite');
    const store = transaction.objectStore(STORES.HIGHLIGHTS);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// 随笔操作
// ========================================

export async function addEssay(essay) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.ESSAYS], 'readwrite');
    const store = transaction.objectStore(STORES.ESSAYS);

    const essayData = {
      id: generateId(),
      bookId: essay.bookId || null,
      bookTitle: essay.bookTitle || null,
      title: essay.title || '',
      content: essay.content || '',
      blocks: essay.blocks || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const request = store.add(essayData);
    request.onsuccess = () => resolve(essayData);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllEssays() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.ESSAYS], 'readonly');
    const store = transaction.objectStore(STORES.ESSAYS);
    const request = store.getAll();
    request.onsuccess = () => {
      const essays = request.result.sort((a, b) =>
        new Date(b.updatedAt) - new Date(a.updatedAt)
      );
      resolve(essays);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function updateEssay(id, updates) {
  const database = getDB();
  return new Promise(async (resolve, reject) => {
    const transaction = database.transaction([STORES.ESSAYS], 'readonly');
    const store = transaction.objectStore(STORES.ESSAYS);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const essay = getRequest.result;
      if (!essay) {
        reject(new Error('Essay not found'));
        return;
      }

      const writeTransaction = database.transaction([STORES.ESSAYS], 'readwrite');
      const writeStore = writeTransaction.objectStore(STORES.ESSAYS);
      const updatedEssay = {
        ...essay,
        ...updates,
        updatedAt: new Date().toISOString()
      };
      const putRequest = writeStore.put(updatedEssay);
      putRequest.onsuccess = () => resolve(updatedEssay);
      putRequest.onerror = () => reject(putRequest.error);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

export async function deleteEssay(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.ESSAYS], 'readwrite');
    const store = transaction.objectStore(STORES.ESSAYS);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// 阅读进度操作
// ========================================

export async function saveReadingProgress(bookId, progress) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.READING_PROGRESS], 'readwrite');
    const store = transaction.objectStore(STORES.READING_PROGRESS);
    const getRequest = store.get(bookId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result || {};
      const progressData = {
        ...existing,
        bookId,
        ...progress,
        updatedAt: new Date().toISOString()
      };

      const putRequest = store.put(progressData);
      putRequest.onsuccess = () => resolve(progressData);
      putRequest.onerror = () => reject(putRequest.error);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

export async function getReadingProgress(bookId) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.READING_PROGRESS], 'readonly');
    const store = transaction.objectStore(STORES.READING_PROGRESS);
    const request = store.get(bookId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// 书签操作
// ========================================

export async function addBookmark(bookmark) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.BOOKMARKS], 'readwrite');
    const store = transaction.objectStore(STORES.BOOKMARKS);

    const bookmarkData = {
      id: generateId(),
      bookId: bookmark.bookId,
      bookTitle: bookmark.bookTitle,
      location: bookmark.location,
      label: bookmark.label || '',
      page: bookmark.page || null,
      chapter: bookmark.chapter || null,
      createdAt: new Date().toISOString()
    };

    const request = store.add(bookmarkData);
    request.onsuccess = () => resolve(bookmarkData);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllBookmarks() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.BOOKMARKS], 'readonly');
    const store = transaction.objectStore(STORES.BOOKMARKS);
    const request = store.getAll();
    request.onsuccess = () => {
      const bookmarks = request.result.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      resolve(bookmarks);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function getBookmarksByBook(bookId) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.BOOKMARKS], 'readonly');
    const store = transaction.objectStore(STORES.BOOKMARKS);
    const index = store.index('bookId');
    const request = index.getAll(bookId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteBookmark(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.BOOKMARKS], 'readwrite');
    const store = transaction.objectStore(STORES.BOOKMARKS);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// 音乐操作
// ========================================

export async function addMusic(music) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MUSIC], 'readwrite');
    const store = transaction.objectStore(STORES.MUSIC);

    const musicData = {
      id: generateId(),
      title: music.title || '未知歌曲',
      artist: music.artist || '未知艺术家',
      album: music.album || '',
      cover: music.cover || null,
      duration: music.duration || 0,
      fileData: music.fileData,
      fileType: music.fileType || 'audio/mpeg',
      fileName: music.fileName || '',
      url: music.url && !music.fileData ? music.url : null,
      favorite: music.favorite || false,
      isPodcast: !!music.isPodcast,
      playCount: 0,
      addedAt: new Date().toISOString(),
      lastPlayedAt: null
    };

    const request = store.add(musicData);
    request.onsuccess = () => resolve(musicData);
    request.onerror = () => reject(request.error);
  });
}

export async function getMusic(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MUSIC], 'readonly');
    const store = transaction.objectStore(STORES.MUSIC);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllMusic() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MUSIC], 'readonly');
    const store = transaction.objectStore(STORES.MUSIC);
    const request = store.getAll();
    request.onsuccess = () => {
      const music = request.result.sort((a, b) =>
        new Date(b.addedAt) - new Date(a.addedAt)
      );
      resolve(music);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function updateMusic(id, updates) {
  const database = getDB();
  return new Promise(async (resolve, reject) => {
    const music = await getMusic(id);
    if (!music) {
      reject(new Error('Music not found'));
      return;
    }

    const transaction = database.transaction([STORES.MUSIC], 'readwrite');
    const store = transaction.objectStore(STORES.MUSIC);
    const updatedMusic = { ...music, ...updates };
    const request = store.put(updatedMusic);
    request.onsuccess = () => resolve(updatedMusic);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteMusic(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MUSIC], 'readwrite');
    const store = transaction.objectStore(STORES.MUSIC);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function searchMusic(query) {
  const allMusic = await getAllMusic();
  const lowerQuery = query.toLowerCase();
  return allMusic.filter(m =>
    m.title.toLowerCase().includes(lowerQuery) ||
    m.artist.toLowerCase().includes(lowerQuery) ||
    (m.album && m.album.toLowerCase().includes(lowerQuery))
  );
}

export async function getFavoriteMusic() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MUSIC], 'readonly');
    const store = transaction.objectStore(STORES.MUSIC);
    const index = store.index('favorite');
    const request = index.getAll(true);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function clearAllMusic() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MUSIC], 'readwrite');
    const store = transaction.objectStore(STORES.MUSIC);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function addPodcast(podcast) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PODCASTS], 'readwrite');
    const store = transaction.objectStore(STORES.PODCASTS);

    const data = {
      id: generateId(),
      feedUrl: podcast.feedUrl,
      title: podcast.title || '未知播客',
      description: podcast.description || '',
      image: podcast.image || null,
      episodes: podcast.episodes || [],
      addedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const request = store.add(data);
    request.onsuccess = () => resolve(data);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllPodcasts() {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PODCASTS], 'readonly');
    const store = transaction.objectStore(STORES.PODCASTS);
    const request = store.getAll();
    request.onsuccess = () => {
      const podcasts = request.result.sort((a, b) =>
        new Date(b.updatedAt || b.addedAt) - new Date(a.updatedAt || a.addedAt)
      );
      resolve(podcasts);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function updatePodcast(id, updates) {
  const database = getDB();
  return new Promise(async (resolve, reject) => {
    const transaction = database.transaction([STORES.PODCASTS], 'readwrite');
    const store = transaction.objectStore(STORES.PODCASTS);
    const request = store.get(id);
    request.onsuccess = () => {
      const current = request.result;
      if (!current) {
        reject(new Error('Podcast not found'));
        return;
      }
      const next = {
        ...current,
        ...updates,
        updatedAt: new Date().toISOString()
      };
      const putReq = store.put(next);
      putReq.onsuccess = () => resolve(next);
      putReq.onerror = () => reject(putReq.error);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function deletePodcast(id) {
  const database = getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PODCASTS], 'readwrite');
    const store = transaction.objectStore(STORES.PODCASTS);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export { STORES };
